// frontend/src/pages/Deposit.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";

const COIN_OPTIONS = [
  { label: "Bitcoin", value: "BTC" },
  { label: "Ethereum", value: "ETH" },
  { label: "USDT", value: "USDT" },
];

const NETWORK_OPTIONS = {
  BTC: [{ label: "BTC", value: "BTC" }],
  ETH: [{ label: "ETH", value: "ETH" }],
  USDT: [
    { label: "ERC20", value: "USDT_ERC20" },
    { label: "BEP20", value: "USDT_BEP20" },
    { label: "TRC20", value: "USDT_TRC20" },
  ],
};

// ---- helpers (date, status, amount) -----------------------------------------
const pickBestDate = (tx) => {
  const candidates = [
    tx?.createdAt,
    tx?.updatedAt,
    tx?.timestamp,
    tx?.blockTimestamp,
    tx?.meta?.blockTimestamp,
  ].filter(Boolean);

  for (const v of candidates) {
    const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const formatDateLocal = (tx) => {
  const d = pickBestDate(tx);
  if (!d) return "—";
  return d.toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kolkata",
  });
};

const normalizeStatus = (raw) => {
  const s = String(raw || "").toLowerCase();
  if (["successful", "success", "confirmed"].includes(s)) return "confirmed";
  if (["pending"].includes(s)) return "pending";
  if (["failed", "error", "reversed", "ignored", "cancelled"].includes(s))
    return "failed";
  return s || "pending";
};

const badgeClasses = (norm) => {
  if (norm === "confirmed")
    return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300";
  if (norm === "pending")
    return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300";
  return "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
};

const formatAmount = (asset, val) => {
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val ?? "0");
  if (asset === "ETH" || asset === "BTC") return n.toFixed(8).replace(/\.?0+$/, "");
  return n.toFixed(6).replace(/\.?0+$/, "");
};

const shortHash = (h) => {
  const s = String(h || "");
  if (!s) return "—";
  if (s.length <= 18) return s;
  return `${s.slice(0, 10)}…${s.slice(-6)}`;
};

const getExplorerUrl = (tx) => {
  const txid = tx?.txid;
  if (!txid) return "";

  const net = String(tx?.network || "").toUpperCase();

  if (net === "BTC") return `https://blockstream.info/tx/${txid}`;
  if (net === "TRC20") return `https://tronscan.org/#/transaction/${txid}`;

  if (net === "BEP20") return `https://bscscan.com/tx/${txid}`;
  if (net === "ERC20" || net === "NATIVE") return `https://etherscan.io/tx/${txid}`;

  return `https://etherscan.io/tx/${txid}`;
};

// ----- compliance helpers ----------------------------------------------------
const getRiskLevel = (tx) => {
  const v =
    tx?.compliance?.riskLevel ??
    tx?.compliance?.risk ??
    tx?.riskLevel ??
    tx?.risk ??
    tx?.scorechain?.riskLevel ??
    tx?.kyt?.riskLevel;
  const n = Number(v);
  return Number.isFinite(n) ? n : null; // null => not screened yet
};

const getHeld = (tx) => {
  const v = tx?.compliance?.held ?? tx?.compliance?.isHeld ?? tx?.held;
  return v === true;
};

const getSenderDetailsStatus = (tx) => {
  // Canonical storage: tx.compliance.userDetails
  // Policy: required for ALL deposits unless explicitly turned off
  if (tx?.compliance?.userDetails?.required === false) return "not_required";
  if (tx?.compliance?.requiresUserDetails === false) return "not_required";
  return "required";
};

const isSenderDetailsSubmitted = (tx) => {
  if (tx?.compliance?.userDetails?.submitted === true) return true;
  const ud = tx?.compliance?.userDetails;
  if (ud && typeof ud === "object") {
    const hasAny =
      !!ud?.senderName ||
      !!ud?.senderPanOrNationalId ||
      !!ud?.senderPlatform ||
      !!ud?.senderPermanentAddress ||
      !!ud?.purpose;
    if (hasAny) return true;
  }
  return false;
};

// ----- NEW: build self-transfer autofill payload from whoami user -------------
const buildSelfTransferAutofill = (u) => {
  const safeStr = (v) => (v === undefined || v === null ? "" : String(v));

  // Name preference: Aadhaar KYC name -> PAN name -> first+last -> account holder -> ""
  const nameFromAadhaar = safeStr(u?.kyc?.aadhaar?.name || u?.aadhaar?.name);
  const nameFromPan = safeStr(u?.kyc?.pan?.name || u?.pan?.name);
  const nameFromProfile = [safeStr(u?.firstName), safeStr(u?.lastName)].filter(Boolean).join(" ").trim();
  const nameFromBank = safeStr(u?.accountHolder);

  const senderName =
    nameFromAadhaar ||
    nameFromPan ||
    nameFromProfile ||
    nameFromBank ||
    "";

  // PAN preference: KYC pan.number -> panCardNumber -> ""
  const senderPanOrId =
    safeStr(
      u?.kyc?.pan?.number ||
      u?.kyc?.pan?.panNumber ||
      u?.kyc?.pan?.pan_no ||
      u?.kyc?.panNumber ||
      u?.pan?.number ||
      u?.pan?.panNumber ||
      u?.panNumber ||
      u?.panCardNumber ||
      u?.pan_card_number ||
      u?.kyc?.panCardNumber ||
      ""
    ) || "";


  // Zip preference: Aadhaar zip -> "" (user can edit if missing)
  const senderZipcode =
    safeStr(u?.kyc?.aadhaar?.zip || u?.aadhaar?.zip) ||
    "";

  // State/address may be in different shapes; keep best-effort
  const state =
    safeStr(u?.kyc?.aadhaar?.address?.state || u?.aadhaar?.address?.state) ||
    safeStr(u?.kyc?.aadhaar?.state || u?.aadhaar?.state) ||
    safeStr(u?.state) ||
    "";

  // Address: try full formatted / lines; fallback to empty
  const addrObj = u?.kyc?.aadhaar?.address || u?.aadhaar?.address || {};
  const addressCandidates = [
    safeStr(addrObj?.fullAddress),
    safeStr(addrObj?.formatted),
    safeStr(addrObj?.address),
    safeStr(addrObj?.line1),
    safeStr(addrObj?.line2),
    safeStr(addrObj?.house),
    safeStr(addrObj?.street),
    safeStr(addrObj?.locality),
    safeStr(addrObj?.city),
    safeStr(addrObj?.district),
  ].filter(Boolean);

  // If we only have pieces, join nicely; if nothing, empty
  let senderPermanentAddress = "";
  if (addressCandidates.length > 0) {
    // If fullAddress/formatted exist, use the first one (already complete)
    const first = addressCandidates[0];
    if (first.length >= 10 && (first.includes(",") || first.includes(" "))) {
      senderPermanentAddress = first;
    } else {
      // join unique tokens
      const uniq = Array.from(new Set(addressCandidates));
      senderPermanentAddress = uniq.join(", ");
    }
  }

  return {
    senderName: senderName.trim(),
    senderPanOrId: senderPanOrId.trim(),
    senderPermanentAddress: senderPermanentAddress.trim(),
    senderState: state.trim(),
    senderZipcode: senderZipcode.trim(),
  };
};

export default function Deposit() {
  const navigate = useNavigate();

  const [tab, setTab] = useState("Crypto");

  const [wallets, setWallets] = useState({
    BTC: "Loading...",
    ETH: "Loading...",
    USDT_ERC20: "Loading...",
    USDT_BEP20: "Loading...",
    USDT_TRC20: "Loading...",
  });
  const [history, setHistory] = useState([]);

  const [selectedCoin, setSelectedCoin] = useState("BTC");
  const [selectedNetwork, setSelectedNetwork] = useState(NETWORK_OPTIONS.BTC[0].value);
  const [copied, setCopied] = useState("");

  // NEW: store whoami user for self-transfer autofill
  const [whoamiUser, setWhoamiUser] = useState(null);

  const [submitModal, setSubmitModal] = useState({
    open: false,
    tx: null,

    // New FIU/Merkle sender details
    isSelfTransfer: false,
    senderName: "",
    senderPanOrId: "",
    senderPlatform: "",
    senderPermanentAddress: "",
    senderState: "",
    senderZipcode: "",
    purpose: "",
    confirmAddressAccurate: false,
  });

  const fetchAll = async () => {
    try {
      const who = await axios.get("/whoami");
      const u = who.data.user || {};
      setWhoamiUser(u);

      setWallets({
        BTC: u.btcAddress || "Error",
        ETH: u.ethAddress || "Error",
        USDT_ERC20: u.ethAddress || "Error",
        USDT_BEP20: u.bscAddress || "Error",
        USDT_TRC20: u.tronAddress || "Error",
      });

      const res = await axios.get("/deposits");
      const rows = Array.isArray(res.data?.deposits) ? res.data.deposits : [];

      const normalized = rows.map((tx) => {
        const risk = getRiskLevel(tx);
        const held = getHeld(tx);
        const senderStatus = getSenderDetailsStatus(tx);
        const senderSubmitted = isSenderDetailsSubmitted(tx);

        const statusNorm = normalizeStatus(tx.status);

        // show submit if sender details required and not yet submitted
        // (we require this for *all* deposits, even if already credited)
        const showSubmit = senderStatus === "required" && senderSubmitted === false;

        return {
          ...tx,
          _key: tx._id || `${tx.asset || "ASSET"}-${tx.txid || Math.random()}`,
          _statusNorm: statusNorm,
          _when: formatDateLocal(tx),
          _amountFmt: formatAmount(tx.asset, tx.amount ?? tx.amountAtomic),
          _assetWithNet:
            tx.asset && tx.network ? `${tx.asset} (${tx.network})` : tx.asset || "—",
          _txid: tx.txid || "",
          _txUrl: getExplorerUrl(tx),

          // compliance UI fields
          _riskLevel: risk,
          _held: held,
          _senderStatus: senderStatus, // required | submitted | not_required
          _senderSubmitted: senderSubmitted,
          _showSubmit: showSubmit,
        };
      });

      setHistory(normalized);
    } catch (err) {
      console.error("Failed to fetch deposit data:", err);
      toast.error("Could not load deposit info. Please log in.");
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCoinChange = (coin) => {
    setSelectedCoin(coin);
    const first = NETWORK_OPTIONS[coin]?.[0]?.value || "";
    setSelectedNetwork(first);
  };

  const activeNetworkLabel = useMemo(() => {
    if (selectedCoin !== "USDT") return selectedCoin;
    const opt = NETWORK_OPTIONS.USDT.find((n) => n.value === selectedNetwork);
    return `USDT (${opt?.label || ""})`;
  }, [selectedCoin, selectedNetwork]);

  const handleCopy = () => {
    const addr = wallets[selectedNetwork];
    if (!addr || addr === "Loading..." || addr === "Error") {
      toast.error("No valid address to copy.");
      return;
    }
    navigator.clipboard
      .writeText(addr)
      .then(() => {
        setCopied(selectedNetwork);
        toast.success("Address copied!");
        setTimeout(() => setCopied(""), 1500);
      })
      .catch(() => toast.error("Copy failed"));
  };

  const openSubmit = (tx) => {
    const existing = tx?.senderDetails || {};
    setSubmitModal({
      open: true,
      tx,

      isSelfTransfer: !!existing?.isSelfTransfer,
      senderName: existing?.senderName || "",
      senderPanOrId: existing?.senderPanOrId || "",
      senderPlatform: existing?.senderPlatform || "",
      senderPermanentAddress: existing?.senderPermanentAddress || "",
      senderState: existing?.senderState || "",
      senderZipcode: existing?.senderZipcode || "",
      purpose: existing?.purpose || "",
      confirmAddressAccurate: !!existing?.confirmAddressAccurate,
    });

    // NEW: if opening modal with self-transfer already enabled, autofill immediately
    if (existing?.isSelfTransfer) {
      const auto = buildSelfTransferAutofill(whoamiUser || {});
      setSubmitModal((s) => ({
        ...s,
        open: true,
        tx,
        isSelfTransfer: true,
        senderName: auto.senderName || s.senderName,
        senderPanOrId: auto.senderPanOrId || s.senderPanOrId,
        senderPermanentAddress: auto.senderPermanentAddress || s.senderPermanentAddress,
        senderState: auto.senderState || s.senderState,
        senderZipcode: auto.senderZipcode || s.senderZipcode,
      }));
    }
  };

  const closeSubmit = () => {
    setSubmitModal({
      open: false,
      tx: null,
      isSelfTransfer: false,
      senderName: "",
      senderPanOrId: "",
      senderPlatform: "",
      senderPermanentAddress: "",
      senderState: "",
      senderZipcode: "",
      purpose: "",
      confirmAddressAccurate: false,
    });
  };

  const submitDetails = async () => {
    const tx = submitModal.tx;
    if (!tx?._id) return;

    // NEW: if self-transfer, ensure we submit autofilled name/pan/address/zip/state (not blanks)
    let payloadSenderName = submitModal.senderName;
    let payloadSenderPanOrId = submitModal.senderPanOrId;
    let payloadSenderPermanentAddress = submitModal.senderPermanentAddress;
    let payloadSenderState = submitModal.senderState;
    let payloadSenderZipcode = submitModal.senderZipcode;

    if (submitModal.isSelfTransfer) {
      const auto = buildSelfTransferAutofill(whoamiUser || {});
      payloadSenderName = (payloadSenderName || auto.senderName || "").trim();
      payloadSenderPanOrId = (payloadSenderPanOrId || auto.senderPanOrId || "").trim();
      payloadSenderPermanentAddress = (payloadSenderPermanentAddress || auto.senderPermanentAddress || "").trim();
      payloadSenderState = (payloadSenderState || auto.senderState || "").trim();
      payloadSenderZipcode = (payloadSenderZipcode || auto.senderZipcode || "").trim();
    }

    // For self-transfer, backend auto-fills senderName/PAN/Address from user KYC,
    // but senderPlatform/purpose/state/zipcode and confirm checkbox are still required.
    if (!submitModal.isSelfTransfer) {
      if (!submitModal.senderName || submitModal.senderName.trim().length < 2) {
        toast.error("Please enter Sender Name.");
        return;
      }
      if (!submitModal.senderPanOrId || submitModal.senderPanOrId.trim().length < 4) {
        toast.error("Please enter Sender PAN / National ID.");
        return;
      }
      if (!submitModal.senderPermanentAddress || submitModal.senderPermanentAddress.trim().length < 6) {
        toast.error("Please enter Sender Permanent Address.");
        return;
      }
    } else {
      // If self-transfer but autofill still empty, guide user (do not submit blanks)
      if (!payloadSenderName || payloadSenderName.length < 2) {
        toast.error("Your KYC name could not be auto-filled. Please uncheck self-transfer and enter Sender Name.");
        return;
      }
      if (!payloadSenderPanOrId || payloadSenderPanOrId.length < 4) {
        toast.error("Your PAN could not be auto-filled. Please uncheck self-transfer and enter Sender PAN / National ID.");
        return;
      }
      if (!payloadSenderPermanentAddress || payloadSenderPermanentAddress.length < 6) {
        toast.error("Your address could not be auto-filled. Please uncheck self-transfer and enter Sender Permanent Address.");
        return;
      }
    }

    if (!submitModal.senderPlatform || submitModal.senderPlatform.trim().length < 2) {
      toast.error("Please enter Sender Platform (e.g., Binance / Self-custody).");
      return;
    }
    if (!payloadSenderState || payloadSenderState.trim().length < 2) {
      toast.error("Please enter Sender State.");
      return;
    }
    if (!payloadSenderZipcode || payloadSenderZipcode.trim().length < 3) {
      toast.error("Please enter Sender Zipcode.");
      return;
    }
    if (!submitModal.purpose || submitModal.purpose.trim().length < 2) {
      toast.error("Please enter Purpose.");
      return;
    }
    if (!submitModal.confirmAddressAccurate) {
      toast.error("Please confirm that the address details are accurate.");
      return;
    }

    try {
      // ✅ deposit router: POST /deposits/:id/sender-details
      await axios.post(`/deposits/${tx._id}/sender-details`, {
        isSelfTransfer: !!submitModal.isSelfTransfer,
        senderName: (payloadSenderName || "").trim(),
        senderPanOrId: (payloadSenderPanOrId || "").trim(),
        // send both keys so backend + admin always persist/show PAN correctly
        senderPanOrNationalId: (payloadSenderPanOrId || "").trim(),
        senderPlatform: submitModal.senderPlatform.trim(),
        senderPermanentAddress: (payloadSenderPermanentAddress || "").trim(),
        senderState: (payloadSenderState || "").trim(),
        senderZipcode: (payloadSenderZipcode || "").trim(),
        purpose: submitModal.purpose.trim(),
        confirmAddressAccurate: !!submitModal.confirmAddressAccurate,
      });

      toast.success("Details submitted. Deposit will be credited after compliance & confirmations.");
      closeSubmit();
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.error || e?.response?.data?.message || "Failed to submit details");
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-slate-900 md:pb-10 pb-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Header */}
      <header className="bg-white dark:bg-slate-950 shadow-lg border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto w-full md:px-6 px-4 md:py-6 py-4 flex flex-col sm:flex-row justify-between items-start sm:items-center">
          <div>
            <h1 className="md:text-3xl text-2xl font-bold text-blue-800 dark:text-blue-300 md:mb-1 mb-0.5">
              Deposit
            </h1>
            <p className="text-blue-600 dark:text-blue-200/80 md:text-base text-sm">
              Select INR or Crypto. For crypto, choose coin & network, then copy the address.
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto w-full md:px-6 px-3 md:mt-6 mt-3 md:space-y-8 space-y-3">
        {/* Quick Actions */}
        <motion.div
          className="bg-white dark:bg-slate-950 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-3 md:flex md:items-center md:justify-between md:gap-4 border-t-4 md:border-t-0 md:border-l-4 border-blue-500 sticky top-0 z-10 md:static backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:supports-[backdrop-filter]:bg-slate-950/80"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-blue-700 dark:text-blue-300 font-medium md:mb-0 mb-2 text-sm md:text-base">
            Quick Actions
          </p>
          <div className="flex flex-wrap gap-2 md:gap-3 md:ml-auto">
            <button
              onClick={() => navigate("/withdraw")}
              className="bg-blue-600 text-white px-3 md:px-5 py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base active:scale-[0.99]"
            >
              Withdraw
            </button>
            <button
              onClick={() => navigate("/exchange")}
              className="bg-blue-600 text-white px-3 md:px-5 py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base active:scale-[0.99]"
            >
              Exchange
            </button>
            <button
              onClick={() => navigate("/p2p")}
              className="bg-blue-600 text-white px-3 md:px-5 py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base active:scale-[0.99]"
            >
              P2P Market
            </button>
            <button
              onClick={() => navigate("/quick-trade")}
              className="bg-blue-600 text-white px-3 md:px-5 py-2 rounded-lg md:rounded-xl hover:bg-blue-700 transition shadow-sm text-xs md:text-base active:scale-[0.99]"
            >
              Quick Trade
            </button>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div
          className="bg-white dark:bg-slate-950 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-4 border-t-4 border-blue-500"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <nav className="flex justify-center gap-6 border-b border-slate-200 dark:border-slate-800 pb-2 md:mb-4 mb-3">
            {["Crypto", "INR"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1.5 text-sm md:text-base font-medium rounded-t-md transition-colors ${
                  tab === t
                    ? "text-blue-700 dark:text-blue-300 border-b-2 border-blue-600"
                    : "text-slate-600 dark:text-slate-300 hover:text-blue-700 dark:hover:text-blue-300"
                }`}
              >
                {t}
              </button>
            ))}
          </nav>

          {/* HOW TO */}
          <div className="bg-blue-50 dark:bg-slate-900/60 border border-blue-100 dark:border-slate-800 md:rounded-xl rounded-lg md:p-4 p-3 md:mb-6 mb-4">
            {tab === "Crypto" ? (
              <>
                <h2 className="md:text-xl text-lg font-semibold text-blue-800 dark:text-blue-300 md:mb-2 mb-1.5">
                  How to Deposit (Crypto)
                </h2>
                <ol className="list-decimal list-inside text-gray-700 dark:text-slate-200 md:text-base text-sm space-y-1">
                  <li>Choose your coin and the correct network.</li>
                  <li>Copy the deposit address shown below.</li>
                  <li>Send funds from your external wallet/exchange.</li>
                  <li>Wait for network confirmations.</li>
                  <li>
                    If requested, submit Sender Details. Deposit is credited only after:
                    <span className="font-semibold"> Merkle risk check + confirmations + sender details</span>
                    (and admin approval for Risk L3+).
                  </li>
                </ol>
              </>
            ) : (
              <>
                <h2 className="md:text-xl text-lg font-semibold text-blue-800 dark:text-blue-300 md:mb-2 mb-1.5">
                  How to Deposit (INR)
                </h2>
                <ol className="list-decimal list-inside text-gray-700 dark:text-slate-200 md:text-base text-sm space-y-1">
                  <li>Use the bank/UPI details below to make a transfer from your own account.</li>
                  <li>Always use your registered name to avoid delays.</li>
                  <li>Your INR wallet will reflect the deposit once verified.</li>
                </ol>
              </>
            )}
          </div>

          {/* Tab Content */}
          {tab === "Crypto" ? (
            <>
              {/* Coin & Network */}
              <div className="md:flex md:items-start md:space-x-6">
                {/* Coin select */}
                <div className="flex-1 md:mb-0 mb-4">
                  <label className="block text-gray-700 dark:text-slate-200 md:mb-2 mb-1 font-medium md:text-base text-sm">
                    Coin
                  </label>
                  <select
                    value={selectedCoin}
                    onChange={(e) => onCoinChange(e.target.value)}
                    className="w-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg md:p-2 p-2 md:text-base text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {COIN_OPTIONS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Network select */}
                <div className="flex-1">
                  <label className="block text-gray-700 dark:text-slate-200 md:mb-2 mb-1 font-medium md:text-base text-sm">
                    Network
                  </label>
                  <select
                    value={selectedNetwork}
                    onChange={(e) => setSelectedNetwork(e.target.value)}
                    className="w-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg md:p-2 p-2 md:text-base text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {NETWORK_OPTIONS[selectedCoin].map((n) => (
                      <option key={n.value} value={n.value}>
                        {n.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Address & Copy */}
              <div className="mt-4 md:mt-6">
                <div className="flex items-center justify-between md:mb-2 mb-1.5">
                  <h3 className="md:text-lg text-base font-medium text-blue-800 dark:text-blue-300">
                    {activeNetworkLabel}
                  </h3>
                  <button
                    onClick={handleCopy}
                    disabled={copied === selectedNetwork}
                    className={`px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors active:scale-[0.99] ${
                      copied === selectedNetwork
                        ? "bg-green-500 text-white cursor-not-allowed"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    }`}
                  >
                    {copied === selectedNetwork ? "Copied" : "Copy Address"}
                  </button>
                </div>
                <div className="bg-gray-100 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 md:p-3 p-2 rounded-lg break-all font-mono text-gray-800 dark:text-slate-100 md:text-base text-sm select-all">
                  {wallets[selectedNetwork]}
                </div>
                <p className="text-[11px] md:text-xs mt-2 text-slate-600 dark:text-slate-400">
                  Make sure you only send funds on the selected network. Sending on the wrong network may
                  result in loss of funds.
                </p>
              </div>
            </>
          ) : (
            // INR tab content — placeholders
            <div className="grid sm:grid-cols-2 gap-4 md:gap-6">
              <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg md:p-4 p-3">
                <h3 className="text-blue-800 dark:text-blue-300 md:text-lg text-base font-semibold md:mb-2 mb-1.5">
                  Bank Transfer
                </h3>
                <ul className="text-gray-700 dark:text-slate-200 md:text-base text-sm space-y-1">
                  <li>
                    <strong>Account Name:</strong> Coinzetto Technologies Pvt Ltd
                  </li>
                  <li>
                    <strong>Account No.:</strong> XXXX XXXX XXXX
                  </li>
                  <li>
                    <strong>IFSC:</strong> ABCD0123456
                  </li>
                  <li>
                    <strong>Bank:</strong> Example Bank, Bengaluru
                  </li>
                </ul>
                <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-2">
                  Use your registered name; IMPS/NEFT/RTGS supported.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-lg md:p-4 p-3">
                <h3 className="text-blue-800 dark:text-blue-300 md:text-lg text-base font-semibold md:mb-2 mb-1.5">
                  UPI
                </h3>
                <ul className="text-gray-700 dark:text-slate-200 md:text-base text-sm space-y-1">
                  <li>
                    <strong>UPI ID:</strong> coinzetto@okbank
                  </li>
                  <li>
                    <strong>Min Deposit:</strong> ₹1,000
                  </li>
                  <li>
                    <strong>Max/Txn:</strong> ₹200,000
                  </li>
                </ul>
                <p className="text-[11px] text-gray-500 dark:text-slate-400 mt-2">
                  Payments from third-party or business accounts may be delayed/reversed.
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {/* Deposit History */}
        <motion.div
          className="bg-white dark:bg-slate-950 md:rounded-2xl rounded-xl md:shadow-lg shadow md:p-6 p-4 border-t-4 border-blue-500"
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="flex items-center justify-between md:mb-4 mb-3">
            <h2 className="md:text-xl text-lg font-semibold text-blue-800 dark:text-blue-300">
              Recent Deposits
            </h2>
            <button
              onClick={fetchAll}
              className="bg-blue-600 text-white px-3 py-2 rounded-lg text-xs md:text-sm hover:bg-blue-700 transition"
            >
              Refresh
            </button>
          </div>

          {/* Mobile stacked list */}
          <div className="block sm:hidden space-y-3">
            {history.length > 0 ? (
              history.map((tx) => (
                <div
                  key={tx._key}
                  className="border border-slate-200 dark:border-slate-800 rounded-lg p-3"
                >
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-slate-700 dark:text-slate-200">
                      {tx._assetWithNet}
                    </span>
                    <span className="font-semibold text-slate-900 dark:text-slate-100">
                      {tx._amountFmt}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                    {tx._when}
                  </div>

                  {/* Compliance chips */}
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-[11px] font-medium">
                      {tx._riskLevel === null ? "Risk: Pending" : `Risk L${tx._riskLevel}`}
                    </span>
                    {tx._held && (
                      <span className="px-2 py-0.5 rounded bg-red-100 text-red-800 text-[11px] font-medium">
                        Held
                      </span>
                    )}
                    {tx._senderStatus === "required" && !tx._senderSubmitted && (
                      <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 text-[11px] font-medium">
                        Sender details required
                      </span>
                    )}
                    {tx._senderSubmitted && (
                      <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-[11px] font-medium">
                        Details submitted
                      </span>
                    )}
                  </div>

                  {/* Tx Hash */}
                  <div className="mt-2 text-[11px] text-slate-600 dark:text-slate-400">
                    <div className="font-medium text-slate-700 dark:text-slate-200">Tx Hash</div>
                    {tx._txid ? (
                      tx._txUrl ? (
                        <a
                          href={tx._txUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono text-blue-700 dark:text-blue-300 underline break-all"
                        >
                          {tx._txid}
                        </a>
                      ) : (
                        <div className="font-mono break-all">{tx._txid}</div>
                      )
                    ) : (
                      <div>—</div>
                    )}
                  </div>

                  <div className="mt-2 flex items-center justify-between">
                    <span className={`px-2 py-0.5 rounded text-xs capitalize ${badgeClasses(tx._statusNorm)}`}>
                      {tx._statusNorm}
                    </span>

                    {tx._showSubmit && (
                      <button
                        onClick={() => openSubmit(tx)}
                        className="bg-orange-600 text-white px-3 py-1.5 rounded text-[11px] font-medium hover:bg-orange-700 transition"
                      >
                        Submit Details
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center md:py-4 py-3 text-gray-500 dark:text-slate-400">
                No deposits yet.
              </div>
            )}
          </div>

          {/* Table for sm+ */}
          <div className="overflow-x-auto hidden sm:block">
            <table className="min-w-full table-auto md:text-base text-sm">
              <thead>
                <tr className="bg-blue-100 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800">
                  <th className="md:px-4 px-3 md:py-2 py-2 text-left text-slate-800 dark:text-slate-200">
                    Asset
                  </th>
                  <th className="md:px-4 px-3 md:py-2 py-2 text-right text-slate-800 dark:text-slate-200">
                    Amount
                  </th>
                  <th className="md:px-4 px-3 md:py-2 py-2 text-left text-slate-800 dark:text-slate-200">
                    Date &amp; Time
                  </th>
                  <th className="md:px-4 px-3 md:py-2 py-2 text-left text-slate-800 dark:text-slate-200">
                    Tx Hash
                  </th>
                  <th className="md:px-4 px-3 md:py-2 py-2 text-left text-slate-800 dark:text-slate-200">
                    Status
                  </th>
                  <th className="md:px-4 px-3 md:py-2 py-2 text-left text-slate-800 dark:text-slate-200">
                    Compliance
                  </th>
                  <th className="md:px-4 px-3 md:py-2 py-2 text-right text-slate-800 dark:text-slate-200">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.length > 0 ? (
                  history.map((tx) => (
                    <tr
                      key={tx._key}
                      className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900/40"
                    >
                      <td className="md:px-4 px-3 md:py-2 py-2 text-slate-800 dark:text-slate-200">
                        {tx._assetWithNet}
                      </td>
                      <td className="md:px-4 px-3 md:py-2 py-2 text-right text-slate-900 dark:text-slate-100">
                        {tx._amountFmt}
                      </td>
                      <td className="md:px-4 px-3 md:py-2 py-2 text-slate-700 dark:text-slate-300">
                        {tx._when}
                      </td>
                      <td className="md:px-4 px-3 md:py-2 py-2">
                        {tx._txid ? (
                          tx._txUrl ? (
                            <a
                              href={tx._txUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="font-mono text-blue-700 dark:text-blue-300 underline"
                              title={tx._txid}
                            >
                              {shortHash(tx._txid)}
                            </a>
                          ) : (
                            <span className="font-mono" title={tx._txid}>
                              {shortHash(tx._txid)}
                            </span>
                          )
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="md:px-4 px-3 md:py-2 py-2 capitalize">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${badgeClasses(tx._statusNorm)}`}>
                          {tx._statusNorm}
                        </span>
                      </td>

                      <td className="md:px-4 px3 md:py-2 py-2">
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800">
                            {tx._riskLevel === null ? "Risk: Pending" : `Risk L${tx._riskLevel}`}
                          </span>
                          {tx._held && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                              Held
                            </span>
                          )}
                          {tx._senderStatus === "required" && !tx._senderSubmitted && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              Sender details required
                            </span>
                          )}
                          {tx._senderSubmitted && (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                              Details submitted
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="md:px-4 px-3 md:py-2 py-2 text-right">
                        {tx._showSubmit ? (
                          <button
                            onClick={() => openSubmit(tx)}
                            className="bg-orange-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-orange-700 transition"
                          >
                            Submit Details
                          </button>
                        ) : (
                          <span className="text-xs text-slate-500 dark:text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="text-center md:py-4 py-3 text-gray-500 dark:text-slate-400"
                    >
                      No deposits yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </main>

      {/* Submit Sender Details Modal */}
      {submitModal.open && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
          style={{
            paddingBottom: "env(safe-area-inset-bottom)",
            paddingTop: "env(safe-area-inset-top)",
          }}
        >
          {/* Mobile-friendly sheet style: full width, scrollable content */}
          <div className="bg-white dark:bg-slate-950 shadow-2xl w-full sm:w-[min(100%,820px)] border border-slate-200 dark:border-slate-800 rounded-t-2xl sm:rounded-xl max-h-[90vh] overflow-y-auto overscroll-contain">
            {/* Sticky header so close stays reachable on mobile */}
            <div className="sticky top-0 z-10 px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/70 backdrop-blur flex items-start justify-between">
              <div className="pr-3">
                <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  Submit Sender Details (FIU Compliance)
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                  Deposits are credited only after Merkle risk check + confirmations + sender details
                  (and admin approval for Risk L3+).
                </div>
              </div>
              <button
                className="shrink-0 text-sm px-2 py-1 rounded-md text-slate-700 hover:text-slate-900 hover:bg-slate-200/60 dark:text-slate-200 dark:hover:text-white dark:hover:bg-slate-800/70"
                onClick={closeSubmit}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="text-xs text-slate-600 dark:text-slate-400">
                Deposit ID:{" "}
                <span className="font-mono text-slate-900 dark:text-slate-100">
                  {submitModal.tx?._id}
                </span>
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="isSelfTransfer"
                  type="checkbox"
                  checked={submitModal.isSelfTransfer}
                  onChange={(e) => {
                    const checked = e.target.checked;

                    if (checked) {
                      const auto = buildSelfTransferAutofill(whoamiUser || {});
                      setSubmitModal((s) => ({
                        ...s,
                        isSelfTransfer: true,
                        // Auto-fill and show; user can still edit if needed
                        senderName: auto.senderName || s.senderName,
                        senderPanOrId: auto.senderPanOrId || s.senderPanOrId,
                        senderPermanentAddress: auto.senderPermanentAddress || s.senderPermanentAddress,
                        senderState: auto.senderState || s.senderState,
                        senderZipcode: auto.senderZipcode || s.senderZipcode,
                      }));
                    } else {
                      setSubmitModal((s) => ({ ...s, isSelfTransfer: false }));
                    }
                  }}
                  className="mt-1"
                />
                <label htmlFor="isSelfTransfer" className="text-sm text-slate-700 dark:text-slate-200">
                  This deposit is from my own wallet/exchange account (self-transfer)
                </label>
              </div>

              {!submitModal.isSelfTransfer ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">Sender Name *</label>
                    <input
                      className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                      value={submitModal.senderName}
                      onChange={(e) => setSubmitModal((s) => ({ ...s, senderName: e.target.value }))}
                      placeholder="Sender full name"
                    />
                  </div>

                  <div>
                    <label className="text-xs text-slate-600 dark:text-slate-400">Sender PAN / National ID *</label>
                    <input
                      className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                      value={submitModal.senderPanOrId}
                      onChange={(e) => setSubmitModal((s) => ({ ...s, senderPanOrId: e.target.value }))}
                      placeholder="PAN / Passport / National ID"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-xs text-slate-600 dark:text-slate-400">Sender Permanent Address *</label>
                    <textarea
                      className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100 min-h-[90px]"
                      value={submitModal.senderPermanentAddress}
                      onChange={(e) => setSubmitModal((s) => ({ ...s, senderPermanentAddress: e.target.value }))}
                      placeholder="Full permanent address"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-xs text-slate-600 dark:text-slate-400 bg-blue-50 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800 rounded-lg p-3">
                    For self-transfer, your Sender Name / PAN / Address / State / Zipcode are auto-filled from your KYC/profile.
                    Please still provide platform and purpose below. If anything is incorrect, you can edit it here.
                  </div>

                  {/* NEW: show auto-filled fields in clean UI */}
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400">Sender Name (auto-filled)</label>
                      <input
                        className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                        value={submitModal.senderName}
                        onChange={(e) => setSubmitModal((s) => ({ ...s, senderName: e.target.value }))}
                        placeholder="Auto-filled name"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400">Sender PAN / National ID (auto-filled)</label>
                      <input
                        className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                        value={submitModal.senderPanOrId}
                        onChange={(e) => setSubmitModal((s) => ({ ...s, senderPanOrId: e.target.value }))}
                        placeholder="Auto-filled PAN / ID"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="text-xs text-slate-600 dark:text-slate-400">Sender Permanent Address (auto-filled)</label>
                      <textarea
                        className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100 min-h-[90px]"
                        value={submitModal.senderPermanentAddress}
                        onChange={(e) => setSubmitModal((s) => ({ ...s, senderPermanentAddress: e.target.value }))}
                        placeholder="Auto-filled address"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400">Sender State (auto-filled)</label>
                      <input
                        className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                        value={submitModal.senderState}
                        onChange={(e) => setSubmitModal((s) => ({ ...s, senderState: e.target.value }))}
                        placeholder="State"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400">Sender Zipcode (auto-filled)</label>
                      <input
                        className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                        value={submitModal.senderZipcode}
                        onChange={(e) => setSubmitModal((s) => ({ ...s, senderZipcode: e.target.value }))}
                        placeholder="Zipcode / PIN"
                      />
                    </div>
                  </div>
                </>
              )}

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-600 dark:text-slate-400">Sender Platform *</label>
                  <input
                    className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                    value={submitModal.senderPlatform}
                    onChange={(e) => setSubmitModal((s) => ({ ...s, senderPlatform: e.target.value }))}
                    placeholder="Binance / Coinbase / Self-custody / etc."
                  />
                </div>

                <div>
                  <label className="text-xs text-slate-600 dark:text-slate-400">Purpose *</label>
                  <input
                    className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                    value={submitModal.purpose}
                    onChange={(e) => setSubmitModal((s) => ({ ...s, purpose: e.target.value }))}
                    placeholder="Personal transfer / savings / business / etc."
                  />
                </div>

                {!submitModal.isSelfTransfer && (
                  <>
                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400">Sender State *</label>
                      <input
                        className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                        value={submitModal.senderState}
                        onChange={(e) => setSubmitModal((s) => ({ ...s, senderState: e.target.value }))}
                        placeholder="State"
                      />
                    </div>

                    <div>
                      <label className="text-xs text-slate-600 dark:text-slate-400">Sender Zipcode *</label>
                      <input
                        className="border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded px-3 py-2 block text-sm w-full text-slate-900 dark:text-slate-100"
                        value={submitModal.senderZipcode}
                        onChange={(e) => setSubmitModal((s) => ({ ...s, senderZipcode: e.target.value }))}
                        placeholder="Zipcode / PIN"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="confirmAddressAccurate"
                  type="checkbox"
                  checked={submitModal.confirmAddressAccurate}
                  onChange={(e) => setSubmitModal((s) => ({ ...s, confirmAddressAccurate: e.target.checked }))}
                  className="mt-1"
                />
                <label htmlFor="confirmAddressAccurate" className="text-sm text-slate-700 dark:text-slate-200">
                  I confirm the above address details are accurate.
                </label>
              </div>

              {/* Sticky footer buttons on mobile for easier submit */}
              <div
                className="sticky bottom-0 -mx-4 px-4 py-3 bg-white/95 dark:bg-slate-950/95 backdrop-blur border-t border-slate-200 dark:border-slate-800 flex justify-end gap-2"
                style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 12px)" }}
              >
                <button
                  className="bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-800 px-4 py-2 rounded-lg text-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition"
                  onClick={closeSubmit}
                >
                  Cancel
                </button>
                <button
                  className="bg-orange-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-orange-700 transition"
                  onClick={submitDetails}
                >
                  Submit Details
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
